import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, Loader2, CheckCircle2, Sparkles, ShoppingCart, Award, X } from 'lucide-react';
import { useTouchOptimization } from '../hooks/useTouchOptimization';
import { useTouchHandler } from '../hooks/useTouchHandler';
import {
  fetchRedeemableInfo,
  buildRedeemTiers,
  type RedeemableInfo,
  type RedeemTier,
} from '../services/loyaltyRedeemApi';

interface LoyaltyStepContentProps {
  phone: string;
  cartTotal: number;
  onConfirm: (points: number, discount: number) => void;
  onSkip: () => void;
}

/**
 * v5 — Contenu de l'étape "Fidélité" intégrée dans le cadre principal.
 * PAS de wrapper modal : ce composant s'affiche DANS le cadre du Cart,
 * comme les étapes 'type', 'info' et 'payment', pour une continuité visuelle.
 */
export const LoyaltyStepContent: React.FC<LoyaltyStepContentProps> = ({
  phone,
  cartTotal,
  onConfirm,
  onSkip,
}) => {
  const { t } = useTranslation();
  const { getTouchClasses } = useTouchOptimization();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<RedeemableInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);

    fetchRedeemableInfo(phone, cartTotal)
      .then((result) => {
        if (!mounted) return;
        if (!result) {
          setError(t("Vérification impossible. Vous pouvez continuer sans utiliser de points."));
          setLoading(false);
          return;
        }
        setInfo(result);
        setLoading(false);

        // Skip silencieux si canal non autorisé
        if (!result.channel_allowed) {
          console.log('[LoyaltyRedeem] channel_allowed=false → skip silencieux');
          setTimeout(() => onSkip(), 100);
        }
      })
      .catch((e) => {
        if (!mounted) return;
        console.error('[LoyaltyRedeem] Step fetch error', e);
        setError(t("Vérification impossible. Vous pouvez continuer sans utiliser de points."));
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [phone, cartTotal, onSkip, t]);

  const tiers: RedeemTier[] = useMemo(() => {
    if (!info) return [];
    return buildRedeemTiers(info).filter((tier) => tier.discount <= cartTotal);
  }, [info, cartTotal]);

  // === Rendu DIRECT dans le cadre principal (pas de wrapper modal) ===
  return (
    <div className="space-y-6">
      {/* Titre de l'étape, comme les autres étapes (h2 + p) */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center shadow-md">
          <Gift className="text-white" size={24} strokeWidth={2.5} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            {loading
              ? t('Programme de fidélité')
              : info?.found && info?.can_redeem && info?.channel_allowed
              ? t('Vous avez des points !')
              : info?.found
              ? t('Merci de votre fidélité')
              : t('Bienvenue !')}
          </h2>
          <p className="text-gray-500 text-sm">
            {loading
              ? t('Vérification en cours…')
              : error
              ? ''
              : info?.found && info?.can_redeem && info?.channel_allowed
              ? t('Utilisez-les pour réduire votre commande')
              : info?.found
              ? t('Vos points continuent à cumuler')
              : t('Vos points commencent dès maintenant')}
          </p>
        </div>
      </div>

      {/* Contenu selon état */}
      {loading && <LoadingState t={t} />}

      {!loading && error && (
        <ErrorState message={error} onContinue={onSkip} t={t} getTouchClasses={getTouchClasses} />
      )}

      {!loading && !error && info && (
        <>
          {info.found && info.can_redeem && info.channel_allowed && tiers.length > 0 && (
            <ClientWithPoints
              info={info}
              tiers={tiers}
              onConfirm={onConfirm}
              onSkip={onSkip}
              t={t}
              getTouchClasses={getTouchClasses}
            />
          )}

          {info.found && info.channel_allowed && !info.can_redeem && (
            <ClientNotEnoughPoints
              info={info}
              onContinue={onSkip}
              t={t}
              getTouchClasses={getTouchClasses}
            />
          )}

          {!info.found && (
            <NewClient onContinue={onSkip} t={t} getTouchClasses={getTouchClasses} />
          )}
        </>
      )}
    </div>
  );
};

/* ================================================================
 * Sous-composants
 * ================================================================ */

const LoadingState: React.FC<{ t: (k: string) => string }> = ({ t }) => (
  <div className="py-8 text-center">
    <Loader2 size={48} className="mx-auto text-orange-500 animate-spin mb-3" />
    <p className="text-sm text-gray-600">{t('Vérification de votre compte fidélité…')}</p>
  </div>
);

const ErrorState: React.FC<{
  message: string;
  onContinue: () => void;
  t: (k: string) => string;
  getTouchClasses: (cls: string) => string;
}> = ({ message, onContinue, t, getTouchClasses }) => {
  const continueHandler = useTouchHandler({ onPress: onContinue, debounceMs: 300 });
  return (
    <div className="space-y-4 text-center">
      <div className="text-4xl">😕</div>
      <p className="text-sm text-gray-700">{message}</p>
      <button
        {...continueHandler.touchHandlers}
        className={getTouchClasses(`w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-4 px-6 rounded-xl shadow-lg transform transition-all duration-100 ${continueHandler.isPressed ? 'scale-95' : 'hover:scale-[1.02]'}`)}
        style={{ touchAction: 'manipulation' }}
      >
        {t('Continuer vers le paiement')}
      </button>
    </div>
  );
};

// Client avec points utilisables
const ClientWithPoints: React.FC<{
  info: RedeemableInfo;
  tiers: RedeemTier[];
  onConfirm: (points: number, discount: number) => void;
  onSkip: () => void;
  t: (k: string, options?: any) => string;
  getTouchClasses: (cls: string) => string;
}> = ({ info, tiers, onConfirm, onSkip, t, getTouchClasses }) => {
  const customerName = info.customer?.name || '';
  const skipHandler = useTouchHandler({ onPress: onSkip, debounceMs: 300 });

  return (
    <div className="space-y-4">
      {/* Carte points */}
      <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-4 border-2 border-orange-200">
        {customerName && (
          <p className="text-sm text-gray-600 mb-2 text-center">
            👋 {t('Bonjour {{name}} !', { name: customerName })}
          </p>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
              <Award className="text-white" size={20} />
            </div>
            <span className="text-gray-700 font-semibold">{t('Vos points')}</span>
          </div>
          <span className="text-xl font-bold text-orange-600">
            {info.loyalty_points}
          </span>
        </div>
        <p className="text-xs text-center text-gray-600 mt-3">
          {t('= jusqu\'à {{amount}}€ de réduction', {
            amount: info.max_discount.toFixed(2),
          })}
        </p>
      </div>

      {/* Question */}
      <p className="text-center text-sm font-semibold text-gray-700 pt-2">
        {t('Combien de points utiliser ?')}
      </p>

      {/* Paliers */}
      <div className="grid grid-cols-2 gap-2">
        {tiers.map((tier, idx) => (
          <TierButton
            key={idx}
            tier={tier}
            onClick={() => onConfirm(tier.points, tier.discount)}
            t={t}
            getTouchClasses={getTouchClasses}
          />
        ))}
      </div>

      {/* Skip */}
      <button
        {...skipHandler.touchHandlers}
        className={getTouchClasses(`w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-all duration-100 ${skipHandler.isPressed ? 'scale-95' : ''}`)}
        style={{ touchAction: 'manipulation' }}
      >
        {t('Garder mes points pour plus tard')}
      </button>
    </div>
  );
};

const TierButton: React.FC<{
  tier: RedeemTier;
  onClick: () => void;
  t: (k: string, options?: any) => string;
  getTouchClasses: (cls: string) => string;
}> = ({ tier, onClick, t, getTouchClasses }) => {
  const handler = useTouchHandler({ onPress: onClick, debounceMs: 300 });
  return (
    <button
      {...handler.touchHandlers}
      className={getTouchClasses(`relative py-4 px-3 rounded-xl font-semibold transition-all duration-100 ${
        tier.isMax
          ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-lg border-2 border-orange-600'
          : 'bg-white border-2 border-gray-200 text-gray-800 hover:border-orange-300 shadow-sm'
      } ${handler.isPressed ? 'scale-95' : 'hover:scale-[1.02]'}`)}
      style={{ touchAction: 'manipulation' }}
    >
      {tier.isMax && (
        <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-yellow-400 text-orange-800 text-[10px] font-bold rounded-full shadow border border-yellow-500">
          MAX
        </span>
      )}
      <div className="flex flex-col items-center gap-0.5">
        <span className={`text-xs ${tier.isMax ? 'text-orange-100' : 'text-gray-500'} font-normal`}>
          {t('{{points}} pts', { points: tier.points })}
        </span>
        <span className="text-xl font-bold">
          −{tier.discount.toFixed(2)}€
        </span>
      </div>
    </button>
  );
};

const ClientNotEnoughPoints: React.FC<{
  info: RedeemableInfo;
  onContinue: () => void;
  t: (k: string, options?: any) => string;
  getTouchClasses: (cls: string) => string;
}> = ({ info, onContinue, t, getTouchClasses }) => {
  const customerName = info.customer?.name || '';
  const continueHandler = useTouchHandler({ onPress: onContinue, debounceMs: 300 });
  return (
    <div className="space-y-4">
      {customerName && (
        <p className="text-sm text-gray-600 text-center">
          👋 {t('Bonjour {{name}} !', { name: customerName })}
        </p>
      )}

      <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-4 border-2 border-orange-200">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
              <Award className="text-white" size={20} />
            </div>
            <span className="text-gray-700 font-semibold">{t('Vos points')}</span>
          </div>
          <span className="text-xl font-bold text-orange-600">
            {info.loyalty_points}
          </span>
        </div>
        <p className="text-xs text-gray-600 mt-2 leading-relaxed">
          {t('Pas encore assez pour une réduction, mais continuez à commander pour en profiter !')}
        </p>
      </div>

      <div className="bg-green-50 rounded-xl p-3 flex items-center gap-2 border border-green-200">
        <CheckCircle2 size={20} className="text-green-600 flex-shrink-0" />
        <p className="text-xs text-green-800 font-medium">
          {t('Cette commande ajoutera des points à votre solde')}
        </p>
      </div>

      <button
        {...continueHandler.touchHandlers}
        className={getTouchClasses(`w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-4 px-6 rounded-xl shadow-lg transform transition-all duration-100 ${continueHandler.isPressed ? 'scale-95' : 'hover:scale-[1.02]'}`)}
        style={{ touchAction: 'manipulation' }}
      >
        {t('Continuer vers le paiement')}
      </button>
    </div>
  );
};

const NewClient: React.FC<{
  onContinue: () => void;
  t: (k: string) => string;
  getTouchClasses: (cls: string) => string;
}> = ({ onContinue, t, getTouchClasses }) => {
  const continueHandler = useTouchHandler({ onPress: onContinue, debounceMs: 300 });
  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-orange-500 via-red-500 to-red-600 rounded-xl p-6 shadow-xl text-center">
        <Sparkles className="text-white mx-auto mb-2" size={36} />
        <p className="text-white text-lg font-bold mb-1">
          {t('Bienvenue dans le programme fidélité !')}
        </p>
        <p className="text-orange-100 text-sm">
          {t('Vos points commencent à se cumuler dès cette commande.')}
        </p>
      </div>

      <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2">
        <ShoppingCart size={20} className="text-orange-500 flex-shrink-0" />
        <p className="text-xs text-gray-700 font-medium">
          {t('Plus vous commandez, plus vous gagnez des réductions')}
        </p>
      </div>

      <button
        {...continueHandler.touchHandlers}
        className={getTouchClasses(`w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-4 px-6 rounded-xl shadow-lg transform transition-all duration-100 ${continueHandler.isPressed ? 'scale-95' : 'hover:scale-[1.02]'}`)}
        style={{ touchAction: 'manipulation' }}
      >
        {t('Continuer vers le paiement')}
      </button>
    </div>
  );
};

/* ================================================================
 * Récap de réduction — affiché en haut de l'étape 'payment'
 * ================================================================ */
interface LoyaltyDiscountRecapProps {
  pointsUsed: number;
  discount: number;
  cartTotal: number;
  finalTotal: number;
  onCancel: () => void;
}
export const LoyaltyDiscountRecap: React.FC<LoyaltyDiscountRecapProps> = ({
  pointsUsed,
  discount,
  cartTotal,
  finalTotal,
  onCancel,
}) => {
  const { t } = useTranslation();
  const cancelHandler = useTouchHandler({ onPress: onCancel, debounceMs: 300 });
  return (
    <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-4 mb-4 border-2 border-orange-200 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="text-xs text-orange-700 font-bold mb-2 flex items-center gap-1.5 uppercase tracking-wide">
            <Sparkles size={14} />
            {t('Réduction fidélité appliquée')}
          </p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>{t('Sous-total')}</span>
              <span>{cartTotal.toFixed(2)}€</span>
            </div>
            <div className="flex justify-between text-orange-600 font-semibold">
              <span>{t('-{{pts}} points', { pts: pointsUsed })}</span>
              <span>−{discount.toFixed(2)}€</span>
            </div>
            <div className="flex justify-between text-gray-900 font-bold pt-2 border-t border-orange-200 mt-2">
              <span>{t('À payer')}</span>
              <span className="text-xl">{finalTotal.toFixed(2)}€</span>
            </div>
          </div>
        </div>
        <button
          {...cancelHandler.touchHandlers}
          className="text-orange-400 hover:text-orange-600 p-1 transition-colors"
          title={t('Annuler la réduction')}
          style={{ touchAction: 'manipulation' }}
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default LoyaltyStepContent;
